import { useState } from 'react';
import clsx from 'clsx';
import { deleteTransactie, type TransactieRecord } from '../../lib/invoices';
import { TransactieList } from './TransactieList';
import { TransactieCreateForm } from './TransactieCreateForm';

interface TransactiesSectionProps {
  parentType: 'Werk_Order__c' | 'Invoice__c';
  parentId: string;
  transacties: TransactieRecord[];
  openAmount: number;
  onMutated: () => void;
}

export function TransactiesSection({
  parentType,
  parentId,
  transacties,
  openAmount,
  onMutated,
}: TransactiesSectionProps) {
  const [formOpen, setFormOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    const confirmed = window.confirm('Transactie verwijderen?');
    if (!confirmed) return;
    setDeletingId(id);
    setDeleteError(null);
    try {
      const result = await deleteTransactie(id);
      if (!result.ok) {
        setDeleteError(result.error);
        return;
      }
      onMutated();
    } finally {
      setDeletingId(null);
    }
  };

  const handleSuccess = () => {
    setFormOpen(false);
    onMutated();
  };

  // Compact bij leeg: alleen de kopregel, geen lege-lijst-placeholder.
  const showList = transacties.length > 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200">
      <div
        className={clsx(
          'px-4 py-3 flex items-center justify-between',
          (showList || formOpen) && 'border-b border-slate-100'
        )}
      >
        <h2 className="text-sm font-semibold text-slate-900">
          Transacties ({transacties.length})
        </h2>
        {!formOpen && (
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
          >
            + Registreer transactie
          </button>
        )}
      </div>

      {showList && (
        <TransactieList
          transacties={transacties}
          onDelete={handleDelete}
          deletingId={deletingId}
        />
      )}

      {deleteError && (
        <div className="mx-4 my-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
          {deleteError}
        </div>
      )}

      {formOpen && (
        <TransactieCreateForm
          parentType={parentType}
          parentId={parentId}
          defaultAmount={openAmount}
          onSuccess={handleSuccess}
          onCancel={() => setFormOpen(false)}
        />
      )}
    </div>
  );
}
